import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, ChevronRight, Clock, Eye, Tag, ArrowLeft, FolderOpen } from 'lucide-react';

const BACKEND_URL = '';

/**
 * Blog archive page for tenant portals.
 * Layout: sidebar categories | search + article grid
 */
export default function TenantBlog({ tenant, basePath }) {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');

  const activeCategory = searchParams.get('category') || '';
  const searchQuery = searchParams.get('q') || '';

  const fetchData = useCallback(async () => {
    if (!tenant?.slug) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeCategory) params.set('category', activeCategory);
      if (searchQuery) params.set('search', searchQuery);
      params.set('status', 'published');

      const [articlesRes, catsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/blog/${tenant.slug}/articles?${params}`),
        fetch(`${BACKEND_URL}/api/blog/${tenant.slug}/categories`)
      ]);

      const articlesData = await articlesRes.json();
      const catsData = await catsRes.json();

      setArticles(articlesData.articles || []);
      setCategories(catsData.categories || []);
    } catch (err) {
      console.error('Failed to load blog:', err);
    } finally {
      setLoading(false);
    }
  }, [tenant?.slug, activeCategory, searchQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(searchParams);
    if (searchInput.trim()) {
      p.set('q', searchInput.trim());
    } else {
      p.delete('q');
    }
    setSearchParams(p);
  };

  const selectCategory = (slug) => {
    const p = new URLSearchParams(searchParams);
    if (slug === activeCategory) {
      p.delete('category');
    } else {
      p.set('category', slug);
    }
    p.delete('q');
    setSearchInput('');
    setSearchParams(p);
  };

  const brandColor = tenant?.branding?.primary_color || '#1a1a1a';

  return (
    <div className="min-h-screen bg-stone-50" data-testid="tenant-blog">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center gap-3 mb-1">
            <Link to={basePath} className="text-stone-400 hover:text-stone-600 transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="text-2xl font-bold text-stone-900" data-testid="blog-title">
              {tenant?.name || 'Blog'}
            </h1>
          </div>
          <p className="text-stone-500 text-sm ml-7">Documentation & Articles</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="hidden md:block w-56 flex-shrink-0" data-testid="blog-sidebar">
            <div className="sticky top-24">
              <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Categories</h3>
              <nav className="space-y-0.5">
                <button
                  onClick={() => selectCategory('')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    !activeCategory
                      ? 'bg-stone-900 text-white font-medium'
                      : 'text-stone-600 hover:bg-stone-100'
                  }`}
                  data-testid="category-all"
                >
                  All Articles
                </button>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => selectCategory(cat.slug)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
                      activeCategory === cat.slug
                        ? 'bg-stone-900 text-white font-medium'
                        : 'text-stone-600 hover:bg-stone-100'
                    }`}
                    data-testid={`category-${cat.slug}`}
                  >
                    <span>{cat.name}</span>
                    {cat.article_count > 0 && (
                      <span className={`text-xs ${activeCategory === cat.slug ? 'text-stone-300' : 'text-stone-400'}`}>
                        {cat.article_count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mb-6" data-testid="blog-search-form">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search articles..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-stone-300 focus:border-transparent"
                  data-testid="blog-search-input"
                />
              </div>
            </form>

            {/* Mobile category pills */}
            <div className="flex gap-2 overflow-x-auto pb-4 md:hidden">
              <button
                onClick={() => selectCategory('')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  !activeCategory ? 'bg-stone-900 text-white' : 'bg-white text-stone-600 border border-stone-200'
                }`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => selectCategory(cat.slug)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    activeCategory === cat.slug ? 'bg-stone-900 text-white' : 'bg-white text-stone-600 border border-stone-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Results info */}
            {(searchQuery || activeCategory) && (
              <div className="mb-4 flex items-center gap-2 text-sm text-stone-500">
                {searchQuery && <span>Results for "<strong className="text-stone-700">{searchQuery}</strong>"</span>}
                {activeCategory && (
                  <span className="flex items-center gap-1">
                    <FolderOpen className="w-3.5 h-3.5" />
                    {categories.find(c => c.slug === activeCategory)?.name || activeCategory}
                  </span>
                )}
                <span>({articles.length} articles)</span>
              </div>
            )}

            {/* Articles Grid */}
            {loading ? (
              <div className="grid gap-4 md:grid-cols-2">
                {[1,2,3,4].map(i => (
                  <div key={i} className="bg-white rounded-xl border border-stone-200 overflow-hidden animate-pulse">
                    <div className="h-40 bg-stone-200" />
                    <div className="p-5 space-y-3">
                      <div className="h-4 bg-stone-200 rounded w-3/4" />
                      <div className="h-3 bg-stone-100 rounded w-full" />
                      <div className="h-3 bg-stone-100 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-16" data-testid="blog-empty">
                <FolderOpen className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <h3 className="text-stone-600 font-medium mb-1">No articles yet</h3>
                <p className="text-stone-400 text-sm">Check back later for new content.</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2" data-testid="articles-grid">
                {articles.map(article => (
                  <Link
                    key={article.id}
                    to={`${basePath}/blog/${article.slug}`}
                    className="group bg-white rounded-xl border border-stone-200 overflow-hidden hover:shadow-md hover:border-stone-300 transition-all"
                    data-testid={`article-card-${article.slug}`}
                  >
                    {article.featured_image && (
                      <div className="h-40 overflow-hidden bg-stone-100">
                        <img
                          src={article.featured_image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      {article.category_slug && (
                        <span
                          className="text-xs font-medium px-2 py-0.5 rounded-full mb-2 inline-block"
                          style={{ backgroundColor: `${brandColor}15`, color: brandColor }}
                        >
                          {categories.find(c => c.slug === article.category_slug)?.name || article.category_slug}
                        </span>
                      )}
                      <h3 className="font-semibold text-stone-900 mb-1.5 group-hover:text-stone-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      {article.excerpt && (
                        <p className="text-stone-500 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-stone-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(article.created_at).toLocaleDateString()}
                        </span>
                        {article.views > 0 && (
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {article.views}
                          </span>
                        )}
                      </div>
                      {article.tags?.length > 0 && (
                        <div className="flex gap-1.5 mt-3 flex-wrap">
                          {article.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="text-xs text-stone-400 flex items-center gap-0.5">
                              <Tag className="w-2.5 h-2.5" />{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="px-5 pb-4">
                      <span className="text-xs font-medium flex items-center gap-1 group-hover:gap-2 transition-all" style={{ color: brandColor }}>
                        Read more <ChevronRight className="w-3 h-3" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
